import { useState, useEffect } from 'react'
import { PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js'
import { StripeError } from '@stripe/stripe-js'
import { Button } from '@/components/ui'
import { RadioGroup, RadioGroupItem, Label } from '@/components/ui'
import { toast } from '@/components/ui'
import axios from 'axios'
import { useSubscription } from '../../hooks/use-subscription'
import { trackAction } from '../../lib/tracking'

interface PaymentFormProps {
  billingInterval: 'monthly' | 'annual'
  onBillingIntervalChange: (interval: 'monthly' | 'annual') => void
  onSuccess?: () => void
  onError?: (error: Error | StripeError) => void
  clientSecrets: {
    monthly: string
    annual: string
  }
  className?: string
}

export function PaymentFormSkeleton({ className = '' }: { className?: string }) {
  return (
    <div className={`space-y-6 ${className}`}>
      <div className="space-y-4">
        <div className="h-12 w-full bg-muted animate-pulse rounded" />
        <div className="space-y-4">
          <div className="h-12 w-full bg-muted animate-pulse rounded" />
          <div className="grid grid-cols-2 gap-4">
            <div className="h-12 bg-muted animate-pulse rounded" />
            <div className="h-12 bg-muted animate-pulse rounded" />
          </div>
        </div>
      </div>
      <div className="h-10 w-full bg-muted animate-pulse rounded" />
    </div>
  )
}

export function PaymentForm({ 
  billingInterval,
  onBillingIntervalChange,
  onSuccess,
  onError,
  clientSecrets,
  className = ''
}: PaymentFormProps) {
  const stripe = useStripe()
  const elements = useElements()
  const [isLoading, setIsLoading] = useState(false)
  const { refresh } = useSubscription()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!stripe || !elements) {
      return
    }

    setIsLoading(true)
    trackAction('payment_form_submitted', {
      billing_interval: billingInterval
    })

    try {
      console.log('Confirming setup for:', billingInterval)
      const { error, setupIntent } = await stripe.confirmSetup({
        elements,
        redirect: 'if_required'
      })

      if (error) {
        console.error('Setup confirmation error:', error)
        onError?.(error)
        trackAction('payment_setup_failed', {
          error_message: error.message || 'Unknown error',
          error_type: error.type || 'unknown',
          billing_interval: billingInterval
        })
        toast.error(error.message)
        return
      }

      if (!setupIntent) {
        console.error('No setup intent returned')
        toast.error('Setup failed')
        trackAction('payment_setup_failed', {
          error_message: 'No setup intent returned',
          error_type: 'missing_setup_intent',
          billing_interval: billingInterval
        })
        return
      }

      // Create subscription with the confirmed setup intent
      const response = await axios.post('/api/integrations/stripe/create-subscription', {
        setupIntentId: setupIntent.id,
        billingInterval
      })

      console.log('Subscription created:', response.data)
      
      // Use refresh from useSubscription hook
      await refresh()
      
      trackAction('payment_setup_completed', {
        billing_interval: billingInterval,
        setup_intent_id: setupIntent.id
      })
      
      toast.success('Subscription created successfully')
      onSuccess?.()
    } catch (error: any) {
      console.error('Setup error:', error)
      trackAction('payment_setup_failed', {
        error_message: error.message || 'Something went wrong',
        error_type: 'api_error',
        billing_interval: billingInterval
      })
      toast.error(error.message || 'Something went wrong')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="space-y-6">
      <RadioGroup
        value={billingInterval}
        onValueChange={onBillingIntervalChange}
        className="flex gap-4"
      >
        <div className="flex items-center space-x-2">
          <RadioGroupItem value="monthly" id="monthly" />
          <Label htmlFor="monthly">Monthly ($19/mo)</Label>
        </div>
        <div className="flex items-center space-x-2">
          <RadioGroupItem value="annual" id="annual" />
          <Label htmlFor="annual">Annual ($99/yr - Save 56%)</Label>
        </div>
      </RadioGroup>

      <form onSubmit={handleSubmit} className={`space-y-6 ${className}`}>
        <PaymentElement 
          options={{
            wallets: {
              applePay: 'auto',
              googlePay: 'auto'
            },
            paymentMethodOrder: ['card', 'apple_pay', 'google_pay', 'link'],
            layout: {
              type: 'tabs',
              defaultCollapsed: false
            }
          }}
        />
        <Button
          type="submit"
          disabled={!stripe || !elements || isLoading}
          className="w-full bg-black hover:bg-black/80 text-white font-medium"
        >
          {isLoading ? 'Processing...' : 'Subscribe Now'}
        </Button>
      </form>
    </div>
  )
} 